import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCategories, deleteCategory, searchCategories, bulkDeleteCategories, bulkUpdateCategoryStatus } from "./categorySlice";
import Swal from 'sweetalert2';
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import log from "../../logger";
import ReactTable from "../../components/ReactTable";
import { paths } from "../../routes/helpers";
import { getImageUrl } from "../../utils/urlUtils";
import useDebounce from "../../hooks/useDebounce";

const CategoryList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total, pages } = useSelector(
    (state) => state.category
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(searchCategories({ keyword: debouncedSearchTerm, page: 1, limit: currentLimit }));
    } else {
      dispatch(fetchCategories({ page: currentPage, limit: currentLimit }));
    }
  }, [dispatch, currentPage, currentLimit, debouncedSearchTerm]);

  // Reset current page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  const handleDelete = (categoryId) => {
    setShowDeleteModal(true);
    setSelectedCategory(categoryId);
  };

  const confirmDelete = async () => {
    if (selectedCategory) {
      try {
        await dispatch(deleteCategory(selectedCategory)).unwrap();
        toast.success("Category deleted successfully!");
        log.info("Category deleted:", selectedCategory);
      } catch (err) {
        toast.error(err || "Failed to delete category");
        log.error("Error deleting category:", err);
      } finally {
        setShowDeleteModal(false);
        setSelectedCategory(null);
      }
    }
  };

  const handleEdit = (categoryId) => {
    navigate(paths.private.category.edit(categoryId));
  };

  const handleCreateCategory = () => {
    navigate(paths.private.category.create());
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1);
    if (debouncedSearchTerm) {
      dispatch(searchCategories({ keyword: debouncedSearchTerm, page: 1, limit: newLimit }));
    } else {
      dispatch(fetchCategories({ page: 1, limit: newLimit }));
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} categor${ids.length > 1 ? 'ies' : 'y'}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await dispatch(bulkDeleteCategories(ids)).unwrap();
      Swal.fire({ title: 'Deleted!', text: `${ids.length} categor${ids.length > 1 ? 'ies' : 'y'} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ title: 'Some deletions failed', text: err || 'One or more items are still in use elsewhere and were not deleted.', icon: 'error' });
    } finally {
      clearSelection();
    }
  };

  const handleBulkStatusChange = (status) => async (ids, clearSelection) => {
    const activating = status === 1;
    const result = await Swal.fire({
      title: `${activating ? 'Activate' : 'Deactivate'} ${ids.length} categor${ids.length > 1 ? 'ies' : 'y'}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${activating ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    dispatch(bulkUpdateCategoryStatus({ ids, status })).then(() => {
      Swal.fire({ title: 'Updated!', text: `${ids.length} categor${ids.length > 1 ? 'ies' : 'y'} ${activating ? 'activated' : 'deactivated'}.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const formatTableData = (categories) => {
    return categories.map((category) => ({
      _id: category._id,
      label: category.label || '-',
      image: getImageUrl(category.image) || null,
      status: category.status ? 'Active' : 'Inactive',
      createdAt: category.createdAt ? new Date(category.createdAt).toLocaleString() : '-',
      updatedAt: category.updatedAt ? new Date(category.updatedAt).toLocaleString() : '-',
    }));
  };

  const headers = [
    { label: 'Image', key: 'image' },
    { label: 'Label', key: 'label' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Updated At', key: 'updatedAt' },
  ];

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  if (error) return (
    <div className="p-4">
      <div className="text-red-500">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Categories</h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreateCategory}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New Category
        </button>
        <div className="text-sm text-gray-600">
          {searchTerm ? `Found ${total} categories` : `Total: ${total} categories`}
        </div>
      </div>

      <ReactTable
        headers={headers}
        data={formatTableData(items)}
        actions={actions}
        onSearch={handleSearch}
        searchDelay={1000}
        searchValue={searchTerm}
        loading={loading}
        pagination={items.length > 0 ? { page: currentPage, limit: currentLimit, total, pages } : null}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        enableRowSelection={true}
        bulkActions={[
          { label: 'Activate Selected', color: 'green', onClick: handleBulkStatusChange(1) },
          { label: 'Deactivate Selected', color: 'gray', onClick: handleBulkStatusChange(0) },
          { label: 'Delete Selected', color: 'red', onClick: handleBulkDelete },
        ]}
      />

      {/* No results message */}
      {items.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg mt-4">
          <p className="text-gray-500">
            {searchTerm ? `No categories found for "${searchTerm}"` : 'No categories found'}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Try a different search term' : 'Click the Create New button to add one'}
          </p>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this category?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default CategoryList; 